import { DestinationEvent, useAffCode } from '@eventbrite/event-renderer';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { NearbyPOITagShape } from '../../../../../types/AppProps';
import { POINTS_OF_INTEREST_AFF_CODE } from '../../../components/EventCardContainer/EventCardContainer';
import { PointOfInterest } from '../../../PointOfInterestPage';

interface FAQ {
    answerText: GenericLazyString;
    questionText: GenericLazyString;
    sectionData?: Array<{
        name: string | GenericLazyString;
        id: string;
        urlPath?: string;
    }>;
}

type FAQReturnType = FAQ | undefined;

type FAQData = {
    hoursOfOperation: PointOfInterest['hoursOfOperation'];
    pointOfInterestName: PointOfInterest['pointOfInterestName'];
    nearbyPointsOfInterest: Array<NearbyPOITagShape>;
    nearbyEvents: Array<DestinationEvent>;
};

export const getHoursOfOperation = (data: FAQData): FAQReturnType => {
    const { hoursOfOperation, pointOfInterestName } = data;
    if (!hoursOfOperation || !hoursOfOperation.length || !pointOfInterestName)
        return undefined;

    return {
        questionText: gettext(
            "What are the %(pointOfInterest)s's hours of operation?",
            {
                pointOfInterest: pointOfInterestName,
            },
        ),
        answerText: gettext('%(pointOfInterest)s hours of operation: ', {
            pointOfInterest: pointOfInterestName,
        }),
        sectionData: hoursOfOperation.map((operatingHours) => ({
            name: operatingHours,
            id: operatingHours,
        })),
    };
};

export const getPopularEvents = (data: FAQData): FAQReturnType => {
    const { nearbyEvents, pointOfInterestName } = data;
    if (!nearbyEvents || !nearbyEvents.length || !pointOfInterestName)
        return undefined;

    return {
        questionText: gettext(
            'What are the best things to do near %(pointOfInterest)s?',
            {
                pointOfInterest: pointOfInterestName,
            },
        ),
        answerText: gettext(
            'Our most popular events near %(pointOfInterest)s include:',
            {
                pointOfInterest: pointOfInterestName,
            },
        ),
        sectionData: nearbyEvents.map(({ id, name, url }) => ({
            name: name,
            urlPath: url
                ? useAffCode(url, POINTS_OF_INTEREST_AFF_CODE)
                : `/e/${id}/`,
            id: id,
        })),
    };
};

export const getNearbyPOI = (data: FAQData): FAQReturnType => {
    const { pointOfInterestName, nearbyPointsOfInterest } = data;
    if (
        !nearbyPointsOfInterest ||
        !nearbyPointsOfInterest.length ||
        !pointOfInterestName
    )
        return undefined;

    return {
        questionText: gettext(
            'What else should I visit around %(pointOfInterest)s?',
            {
                pointOfInterest: pointOfInterestName,
            },
        ),
        answerText: gettext(
            'Other things to do or visit around %(pointOfInterest)s include: ',
            {
                pointOfInterest: pointOfInterestName,
            },
        ),
        sectionData: nearbyPointsOfInterest.map(
            ({ pointOfInterestName, linkUrl }) => ({
                id: linkUrl,
                urlPath: linkUrl,
                name: pointOfInterestName,
            }),
        ),
    };
};

export enum FAQS_ENUM {
    FAQ_HOURS_OF_OPERATION = 'hoursOfOperation',
    FAQ_POPULAR_EVENTS = 'nearbyEvents',
    FAQ_NEARBY_POI = 'nearbyPointsOfInterest',
}

export const FAQS_MAP = {
    [FAQS_ENUM.FAQ_HOURS_OF_OPERATION]: getHoursOfOperation,
    [FAQS_ENUM.FAQ_POPULAR_EVENTS]: getPopularEvents,
    [FAQS_ENUM.FAQ_NEARBY_POI]: getNearbyPOI,
} as const;

export const getFAQs = ({
    data,
    faqs,
}: {
    data: FAQData;
    faqs: Array<FAQS_ENUM>;
}): Array<FAQ> => {
    return faqs.reduce<Array<FAQ>>((faqList, faq) => {
        const fn = FAQS_MAP[faq];
        const result = fn(data);

        if (result) faqList.push(result);
        return faqList;
    }, []);
};
